import { create } from 'zustand';

export type ThemeId = 'emerald' | 'indigo' | 'rose' | 'amber' | 'slate';

export interface ThemeColors {
  primary: string;      // bg-{color}-500
  primaryHover: string;  // bg-{color}-600
  primaryLight: string;  // bg-{color}-50
  primaryLighter: string; // bg-{color}-100
  primaryText: string;   // text-{color}-600
  primaryTextDark: string; // text-{color}-700
  primaryTextDarker: string; // text-{color}-900
  primaryRing: string;   // ring-{color}-500
  primaryBorder: string; // border-{color}-200
  primaryGradientFrom: string;
  primaryGradientTo: string;
}

// Actual CSS hex values for use in inline style={{}} attributes
export interface ThemeHex {
  primary: string;
  primaryHover: string;
  primaryLight: string;
  primaryLighter: string;
  primaryText: string;
  primaryTextDark: string;
  primaryRing: string;
}

export interface Theme {
  id: ThemeId;
  name: string;
  emoji: string;
  colors: ThemeColors;
  hex: ThemeHex;
}

export const THEMES: Theme[] = [
  {
    id: 'emerald',
    name: 'Emerald',
    emoji: '🟢',
    colors: {
      primary: 'bg-emerald-500',
      primaryHover: 'bg-emerald-600',
      primaryLight: 'bg-emerald-50',
      primaryLighter: 'bg-emerald-100',
      primaryText: 'text-emerald-600',
      primaryTextDark: 'text-emerald-700',
      primaryTextDarker: 'text-emerald-900',
      primaryRing: 'ring-emerald-500',
      primaryBorder: 'border-emerald-200',
      primaryGradientFrom: 'from-emerald-50',
      primaryGradientTo: 'to-teal-100',
    },
    hex: {
      primary: '#10b981',
      primaryHover: '#059669',
      primaryLight: '#ecfdf5',
      primaryLighter: '#d1fae5',
      primaryText: '#059669',
      primaryTextDark: '#047857',
      primaryRing: '#10b981',
    },
  },
  {
    id: 'indigo',
    name: 'Indigo',
    emoji: '🔵',
    colors: {
      primary: 'bg-indigo-500',
      primaryHover: 'bg-indigo-600',
      primaryLight: 'bg-indigo-50',
      primaryLighter: 'bg-indigo-100',
      primaryText: 'text-indigo-600',
      primaryTextDark: 'text-indigo-700',
      primaryTextDarker: 'text-indigo-900',
      primaryRing: 'ring-indigo-500',
      primaryBorder: 'border-indigo-200',
      primaryGradientFrom: 'from-indigo-50',
      primaryGradientTo: 'to-purple-100',
    },
    hex: {
      primary: '#6366f1',
      primaryHover: '#4f46e5',
      primaryLight: '#eef2ff',
      primaryLighter: '#e0e7ff',
      primaryText: '#4f46e5',
      primaryTextDark: '#4338ca',
      primaryRing: '#6366f1',
    },
  },
  {
    id: 'rose',
    name: 'Rose',
    emoji: '🌹',
    colors: {
      primary: 'bg-rose-500',
      primaryHover: 'bg-rose-600',
      primaryLight: 'bg-rose-50',
      primaryLighter: 'bg-rose-100',
      primaryText: 'text-rose-600',
      primaryTextDark: 'text-rose-700',
      primaryTextDarker: 'text-rose-900',
      primaryRing: 'ring-rose-500',
      primaryBorder: 'border-rose-200',
      primaryGradientFrom: 'from-rose-50',
      primaryGradientTo: 'to-pink-100',
    },
    hex: {
      primary: '#f43f5e',
      primaryHover: '#e11d48',
      primaryLight: '#fff1f2',
      primaryLighter: '#ffe4e6',
      primaryText: '#e11d48',
      primaryTextDark: '#be123c',
      primaryRing: '#f43f5e',
    },
  },
  {
    id: 'amber',
    name: 'Amber',
    emoji: '🟡',
    colors: {
      primary: 'bg-amber-500',
      primaryHover: 'bg-amber-600',
      primaryLight: 'bg-amber-50',
      primaryLighter: 'bg-amber-100',
      primaryText: 'text-amber-600',
      primaryTextDark: 'text-amber-700',
      primaryTextDarker: 'text-amber-900',
      primaryRing: 'ring-amber-500',
      primaryBorder: 'border-amber-200',
      primaryGradientFrom: 'from-amber-50',
      primaryGradientTo: 'to-yellow-100',
    },
    hex: {
      primary: '#f59e0b',
      primaryHover: '#d97706',
      primaryLight: '#fffbeb',
      primaryLighter: '#fef3c7',
      primaryText: '#d97706',
      primaryTextDark: '#b45309',
      primaryRing: '#f59e0b',
    },
  },
  {
    id: 'slate',
    name: 'Slate',
    emoji: '⚫',
    colors: {
      primary: 'bg-slate-700',
      primaryHover: 'bg-slate-800',
      primaryLight: 'bg-slate-50',
      primaryLighter: 'bg-slate-100',
      primaryText: 'text-slate-600',
      primaryTextDark: 'text-slate-700',
      primaryTextDarker: 'text-slate-900',
      primaryRing: 'ring-slate-500',
      primaryBorder: 'border-slate-200',
      primaryGradientFrom: 'from-slate-50',
      primaryGradientTo: 'to-gray-100',
    },
    hex: {
      primary: '#334155',
      primaryHover: '#1e293b',
      primaryLight: '#f8fafc',
      primaryLighter: '#f1f5f9',
      primaryText: '#475569',
      primaryTextDark: '#334155',
      primaryRing: '#64748b',
    },
  },
];

interface ThemeState {
  themeId: ThemeId;
  theme: Theme;
  setTheme: (id: ThemeId) => void;
}

const getStoredTheme = (): ThemeId => {
  try {
    const stored = localStorage.getItem('plugd_theme');
    if (stored && THEMES.find(t => t.id === stored)) return stored as ThemeId;
  } catch {
    // Ignore localStorage errors
  }
  return 'emerald';
};

const getTheme = (id: ThemeId): Theme => THEMES.find(t => t.id === id) || THEMES[0];

export const useThemeStore = create<ThemeState>((set) => ({
  themeId: getStoredTheme(),
  theme: getTheme(getStoredTheme()),
  setTheme: (id: ThemeId) => {
    localStorage.setItem('plugd_theme', id);
    set({ themeId: id, theme: getTheme(id) });
  },
}));
